import { Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { inr } from "@/lib/format";

function Cell({ value, onChange, err, testid, w = "w-24", placeholder, disabled, right = true }) {
  return (
    <div>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} placeholder={placeholder}
        onFocus={(e) => e.target.select()}
        className={`h-8 px-2 text-xs ${w} ${right ? "text-right money" : ""} ${err ? "border-red-500 bg-red-50" : ""}`} data-testid={testid} />
      {err && <div className="text-[10px] text-red-700 mt-0.5 whitespace-nowrap" data-testid={`${testid}-error`}>{err}</div>}
    </div>
  );
}

const H = ["Employee ID", "Employee Name", "Monthly Salary", "Working Days", "Working Hours", "Daily Salary", "Hourly Salary", "Earned Salary", "Outstanding Advance", "Deduction (₹)", "Final Payable", ""];

export default function PayrollTable({ rows, calcs, method, shift, onChange, onRemove, disabled, advances = {} }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-auto max-h-[620px]" data-testid="payroll-table">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0 z-10">
          <tr>{H.map((h, i) => <th key={h || i} className={`px-2 py-2.5 whitespace-nowrap ${i >= 2 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={H.length} className="text-center text-slate-500 py-8">No employees match.</td></tr>}
          {rows.map((r) => {
            const c = calcs[r._id];
            const bad = Object.keys(c.errors).length > 0;
            const set = (k) => (v) => onChange(r._id, k, v);
            const t = (k) => `payroll-${k}-${r._id}`;
            return (
              <tr key={r._id} className={`border-t border-slate-100 align-top ${bad ? "bg-red-50/40" : "hover:bg-slate-50"}`} data-testid={`payroll-row-${r._id}`}>
                <td className="px-2 py-1.5"><Cell value={r.code} onChange={set("code")} err={c.errors.code} testid={t("code")} w="w-24" right={false} disabled={disabled} /></td>
                <td className="px-2 py-1.5"><Cell value={r.name} onChange={set("name")} err={c.errors.name} testid={t("name")} w="w-40" right={false} disabled={disabled} /></td>
                <td className="px-2 py-1.5"><Cell value={r.salary} onChange={set("salary")} err={c.errors.salary} testid={t("salary")} w="w-24 ml-auto" disabled={disabled} /></td>
                <td className="px-2 py-1.5"><Cell value={r.days} onChange={set("days")} err={c.errors.days} testid={t("days")} w="w-16 ml-auto" disabled={disabled} /></td>
                <td className="px-2 py-1.5">
                  <Cell value={r.hours} onChange={set("hours")} err={c.errors.hours} testid={t("hours")} w="w-20 ml-auto" disabled={disabled}
                    placeholder={method === "hour" && c.hoursUsed != null ? String(+c.hoursUsed.toFixed(2)) : "—"} />
                  {method === "hour" && r.hours === "" && c.hoursUsed != null && <div className="text-[10px] text-slate-400 text-right mt-0.5">days × {shift}</div>}
                </td>
                <td className="px-2 py-2.5 text-right money whitespace-nowrap" data-testid={t("daily")}>{inr(c.daily / 100)}</td>
                <td className="px-2 py-2.5 text-right money whitespace-nowrap" data-testid={t("hourly")}>{c.hourly != null ? inr(c.hourly / 100) : "—"}</td>
                <td className="px-2 py-2.5 text-right money font-semibold whitespace-nowrap" data-testid={t("earned")}>{inr(c.earned / 100)}</td>
                <td className="px-2 py-2.5 text-right money whitespace-nowrap" data-testid={t("advance")}>
                  {(() => {
                    const a = advances[String(r.code || "").trim().toUpperCase()];
                    if (a == null) return <span className="text-slate-400" title="No advance ledger in the app">—</span>;
                    const over = c.deduction > Math.round(a * 100);
                    return <><span className={over ? "text-red-700 font-semibold" : "text-amber-700"}>{inr(a)}</span>{over && <div className="text-[10px] text-red-700" data-testid={`${t("advance")}-over`}>Deduction exceeds advance</div>}</>;
                  })()}
                </td>
                <td className="px-2 py-1.5"><Cell value={r.deduction} onChange={set("deduction")} err={c.errors.deduction} testid={t("deduction")} w="w-24 ml-auto" disabled={disabled} /></td>
                <td className="px-2 py-2.5 text-right money font-bold text-blue-800 whitespace-nowrap" data-testid={t("final")}>{inr(c.final / 100)}</td>
                <td className="px-2 py-1.5">
                  {!disabled && <button onClick={() => onRemove(r._id)} className="p-1.5 rounded text-slate-400 hover:text-red-700 hover:bg-red-50 transition-colors" title="Remove employee" data-testid={t("remove")}><Trash2 className="h-4 w-4" /></button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
